import { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useTranslation } from 'react-i18next';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { AllMissionGQLItemResult, BusinessType } from '@bloomays-lib/types.shared';
import { CardItem, ObjectiveKpiCard, LoaderGlobal } from '@bloomays-lib/ui.shared';
import { DataGridV2, StatusChip, formatEuro } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2Column, DataGridV2LegendItem } from '@bloomays-lib/ui.shared/datagrid';
import {
  MissionRow,
  MissionTab,
  MargeFilter,
  StatutFilter,
  TamFilter,
  TypeFilter,
  buildTamOptions,
  computeMissionKpis,
  deriveMissionRow,
  isDueWithin60,
  isLeavingNextMonth,
  isUnderMargin,
  isUpcoming,
  marginTone,
} from '../../helpers/missionsDashboard';
import MissionDetailPanel from '../molecules/MissionDetailPanel';
import { useAuthStore } from '../../stores';
import { PageHeader } from '../molecules/PageHeader';

export type MissionsDashboardProps = {
  dataMissions: AllMissionGQLItemResult[];
  loading: boolean;
};

const initials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

const fmtPct = (pct: number) => `${Math.round(pct * 100)} %`;
const fmtDate = (d: Date | null) => (d ? convertDateToStringFormat(d, 'dd/MM/yy') : '—');

const typeChipTone = (type: BusinessType): 'info' | 'purple' | 'neutral' =>
  type === BusinessType.IT ? 'info' : type === BusinessType.People ? 'purple' : 'neutral';

const MissionsDashboard = ({ dataMissions, loading }: MissionsDashboardProps) => {
  const { t } = useTranslation(['mission']);

  const connectedEmail = useAuthStore((state) => state.auth?.user?.email);

  const [statut, setStatut] = useState<StatutFilter>('actives');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('tous');
  const [margeFilter, setMargeFilter] = useState<MargeFilter>('toutes');
  const [tamFilter, setTamFilter] = useState<TamFilter>('tous');
  const [tab, setTab] = useState<MissionTab>('toutes');
  const [selected, setSelected] = useState<MissionRow | undefined>();

  const rows = useMemo<MissionRow[]>(() => dataMissions.map((m) => deriveMissionRow(m)), [dataMissions]);

  const tamOptions = useMemo(() => buildTamOptions(rows), [rows]);

  // Filtre par défaut : si le user connecté est TAM d'au moins une mission, on
  // pré-filtre sur ses missions ; sinon on affiche tout. Initialisé une seule
  // fois (le choix manuel de l'utilisateur ne doit pas être écrasé).
  const tamDefaultApplied = useRef(false);
  useEffect(() => {
    if (tamDefaultApplied.current || tamOptions.length === 0) return;
    tamDefaultApplied.current = true;
    const normalizedEmail = connectedEmail?.toLowerCase();
    const match = normalizedEmail ? tamOptions.find((opt) => opt.value.toLowerCase() === normalizedEmail) : undefined;
    if (match) setTamFilter(match.value);
  }, [tamOptions, connectedEmail]);

  // Dimensional filters (Type + Marge + TAM) — also drive the KPIs.
  const dimensionFiltered = useMemo(
    () =>
      rows.filter((r) => {
        if (typeFilter !== 'tous' && r.type !== typeFilter) return false;
        if (margeFilter === 'sous15' && !isUnderMargin(r)) return false;
        if (tamFilter !== 'tous' && r.accountManagerEmail !== tamFilter) return false;
        return true;
      }),
    [rows, typeFilter, margeFilter, tamFilter],
  );

  const kpis = useMemo(() => computeMissionKpis(dimensionFiltered), [dimensionFiltered]);

  // Grid rows = dimensional filters + statut + active tab view.
  const gridRows = useMemo(() => {
    let result = dimensionFiltered;
    if (statut === 'actives') result = result.filter((r) => r.isActive);
    else if (statut === 'terminees') result = result.filter((r) => !r.isActive);
    else if (statut === 'a_venir') result = result.filter((r) => isUpcoming(r));

    if (tab === 'echeances') {
      result = result.filter(isDueWithin60).sort((a, b) => (a.finInDays ?? Infinity) - (b.finInDays ?? Infinity));
    } else if (tab === 'departs') {
      result = result
        .filter((r) => isLeavingNextMonth(r))
        .sort((a, b) => (a.finInDays ?? Infinity) - (b.finInDays ?? Infinity));
    }
    return result;
  }, [dimensionFiltered, statut, tab]);

  const columns: DataGridV2Column<MissionRow>[] = useMemo(
    () => [
      {
        field: 'client',
        header: 'Mission / Client',
        minWidth: 220,
        flex: 1,
        searchText: (row) => `${row.client} ${row.label ?? ''} ${row.recordId}`,
        render: (row) => (
          <TwoLine>
            <strong>{row.client || '—'}</strong>
            <span>{row.label || '—'}</span>
          </TwoLine>
        ),
      },
      {
        field: 'bloomer',
        header: 'Bloomer / Fournisseur',
        minWidth: 200,
        variant: 'avatar',
        searchText: (row) => `${row.bloomer} ${row.fournisseur}`,
        avatar: (row) => ({
          initials: initials(row.bloomer),
          name: row.bloomer || '—',
          subtitle: row.fournisseur || '—',
        }),
      },
      {
        field: 'type',
        header: t('Type'),
        width: 110,
        variant: 'chip',
        chip: (row) => (row.type ? { label: row.type, tone: typeChipTone(row.type) } : null),
      },
      {
        // Filtre par date d'échéance (type natif `date`) au lieu d'un filtre
        // texte sur le nombre de jours restants — cf. TEC-3053.
        field: 'finInDays',
        header: 'Période & échéance',
        minWidth: 190,
        type: 'date',
        valueGetter: (row) => row.end ?? undefined,
        render: (row) => (
          <TwoLine>
            <span>
              {fmtDate(row.begin)} → {fmtDate(row.end)}
            </span>
            <EcheanceBadge row={row} />
          </TwoLine>
        ),
      },
      {
        field: 'vente',
        header: 'TJM vente / achat',
        width: 140,
        align: 'right',
        type: 'number',
        valueGetter: (row) => row.vente,
        render: (row) => (
          <TwoLine $right>
            <strong>{formatEuro(row.vente)}</strong>
            <span>{formatEuro(row.achat)}</span>
          </TwoLine>
        ),
      },
      {
        field: 'margeMonth',
        header: 'Marge €/mois + %',
        width: 150,
        align: 'right',
        type: 'number',
        valueGetter: (row) => row.margeMonth,
        render: (row) => (
          <TwoLine $right>
            <strong>{formatEuro(row.margeMonth)}</strong>
            <StatusChip tone={marginTone(row.margePct)}>{fmtPct(row.margePct)}</StatusChip>
          </TwoLine>
        ),
      },
      {
        // Filtre par liste de valeurs (Active / Terminée) au lieu d'un filtre
        // texte sur un booléen 0/1 — cf. TEC-3053.
        field: 'isActive',
        header: t('isActive'),
        width: 150,
        type: 'singleSelect',
        valueOptions: [
          { value: 'active', label: 'Active' },
          { value: 'terminee', label: 'Terminée' },
        ],
        valueGetter: (row) => (row.isActive ? 'active' : 'terminee'),
        render: (row) => (
          <ChipStack>
            <StatusChip tone={row.isActive ? 'success' : 'neutral'} dot>
              {row.isActive ? 'Active' : 'Terminée'}
            </StatusChip>
            {row.replacement ? <StatusChip tone="warning">Rempl.</StatusChip> : null}
          </ChipStack>
        ),
      },
    ],
    [t],
  );

  const legend: DataGridV2LegendItem[] = [
    { label: 'Marge ≥ 20 %', tone: 'success' },
    { label: 'Marge 15–20 %', tone: 'warning' },
    { label: 'Marge < 15 %', tone: 'danger' },
  ];

  return (
    <>
      {loading ? (
        <LoaderGlobal waitingText="Chargement des Missions, ça va aller très vite..." />
      ) : (
        <>
          <KpiRow>
            <ObjectiveKpiCard
              accent="pink"
              icon={<CardItem variant="pink" size="small" emoji={<CheckCircleOutlinedIcon fontSize="small" />} />}
              title="Missions actives"
              valuePrimary={`${kpis.activeCount}`}
              valueSecondary={` / ${kpis.total}`}
              progressPercent={kpis.total ? (kpis.activeCount / kpis.total) * 100 : 0}
              footer={`${kpis.terminatedCount} terminées · ${kpis.underMarginCount} sous 15 % de marge`}
              footerAccent={kpis.underMarginCount > 0}
              onClick={() => {
                setStatut('actives');
                setTab('toutes');
              }}
              data-testid="kpi-missions-actives"
            />
            <ObjectiveKpiCard
              accent="blue"
              icon={<CardItem variant="blue" size="small" emoji={<TrendingUpOutlinedIcon fontSize="small" />} />}
              title="Marge brute / mois"
              valuePrimary={formatEuro(kpis.margeMonthTotal)}
              progressPercent={kpis.tauxMargeMoyen * 100}
              footer={`Taux de marge moyen : ${fmtPct(kpis.tauxMargeMoyen)}`}
              data-testid="kpi-marge"
            />
            <ObjectiveKpiCard
              accent="blue"
              icon={<CardItem variant="blue" size="small" emoji={<PaymentsOutlinedIcon fontSize="small" />} />}
              title="CA mensuel estimé"
              valuePrimary={formatEuro(kpis.caMonthTotal)}
              progressPercent={kpis.caMonthTotal ? (kpis.margeMonthTotal / kpis.caMonthTotal) * 100 : 0}
              footer={`Achat cumulé ${formatEuro(kpis.achatMonthTotal)} · TJM moyen ${formatEuro(
                kpis.tjmVenteMoyen,
              )} / ${formatEuro(kpis.tjmAchatMoyen)}`}
              data-testid="kpi-ca"
            />
            <ObjectiveKpiCard
              accent="pink"
              icon={
                <CardItem
                  variant="pink"
                  size="small"
                  emoji={
                    kpis.dueWithin30Count > 0 ? (
                      <WarningAmberOutlinedIcon fontSize="small" />
                    ) : (
                      <EventBusyOutlinedIcon fontSize="small" />
                    )
                  }
                />
              }
              title="Échéances < 60 jours"
              valuePrimary={`${kpis.dueWithin60Count}`}
              valueSecondary=" miss."
              progressPercent={kpis.activeCount ? (kpis.dueWithin60Count / kpis.activeCount) * 100 : 0}
              footer={`${kpis.dueWithin30Count} sous 30 jours`}
              footerAccent={kpis.dueWithin30Count > 0}
              onClick={() => {
                setStatut('actives');
                setTab('echeances');
              }}
              data-testid="kpi-echeances"
            />
          </KpiRow>

          <FiltersRow>
            <FilterGroup
              label="Statut"
              value={statut}
              onChange={(v) => setStatut(v as StatutFilter)}
              options={[
                { value: 'actives', label: 'Actives' },
                { value: 'a_venir', label: 'À venir' },
                { value: 'toutes', label: 'Toutes' },
                { value: 'terminees', label: 'Terminées' },
              ]}
            />
            <FilterGroup
              label="Type"
              value={typeFilter}
              onChange={(v) => setTypeFilter(v as TypeFilter)}
              options={[
                { value: 'tous', label: 'Tous' },
                { value: BusinessType.IT, label: 'IT' },
                { value: BusinessType.People, label: 'People' },
              ]}
            />
            <FilterGroup
              label="Marge"
              value={margeFilter}
              onChange={(v) => setMargeFilter(v as MargeFilter)}
              options={[
                { value: 'toutes', label: 'Toutes' },
                { value: 'sous15', label: '< 15 %' },
              ]}
            />
            <FilterGroupRoot>
              <FilterLabel>Account manager</FilterLabel>
              <TamSelect
                value={tamFilter}
                onChange={(e) => setTamFilter(e.target.value)}
                aria-label="Filtrer par account manager"
              >
                <option value="tous">Tous</option>
                {tamOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TamSelect>
            </FilterGroupRoot>
          </FiltersRow>

          <TabsRow>
            {(
              [
                { key: 'toutes', label: 'Toutes les missions' },
                { key: 'echeances', label: 'Échéances < 60j' },
                { key: 'departs', label: 'Départs le mois prochain' },
              ] as { key: MissionTab; label: string }[]
            ).map((item) => (
              <TabButton key={item.key} $active={tab === item.key} onClick={() => setTab(item.key)} type="button">
                {item.label}
              </TabButton>
            ))}
          </TabsRow>

          {tab === 'echeances' ? (
            <Banner $tone="warning">
              Missions actives dont la date de fin tombe dans les 60 prochains jours, triées par échéance la plus
              proche.
            </Banner>
          ) : null}
          {tab === 'departs' ? (
            <Banner $tone="danger">
              Missions dont la date de fin tombe le mois prochain (sorties de mission à anticiper).
            </Banner>
          ) : null}

          <DataGridV2<MissionRow>
            uniqueId="missions-dashboard"
            rows={gridRows}
            columns={columns}
            getRowId={(row) => row.recordId}
            loading={loading}
            search
            searchPlaceholder="Rechercher un client, bloomer, fournisseur, mission…"
            legend={legend}
            getRowTone={(row) => (isUnderMargin(row) && row.isActive ? 'danger' : undefined)}
            onRowClick={(row) => setSelected(row)}
            pageSize={50}
            pagination
          />
        </>
      )}

      <Drawer anchor="right" open={!!selected} onClose={() => setSelected(undefined)}>
        {selected ? <MissionDetailPanel mission={selected} onClose={() => setSelected(undefined)} /> : null}
      </Drawer>
    </>
  );
};

export default MissionsDashboard;
export { MissionsDashboard };

// ── Échéance badge ───────────────────────────────────────────────────────────

const EcheanceBadge = ({ row }: { row: MissionRow }) => {
  if (row.finInDays === null) return <StatusChip tone="neutral">Sans fin</StatusChip>;
  if (row.finInDays < 0) return <StatusChip tone="neutral">Terminée</StatusChip>;
  const tone = row.finInDays <= 30 ? 'danger' : row.finInDays <= 60 ? 'warning' : 'success';
  return <StatusChip tone={tone}>{`Fin dans ${row.finInDays} j`}</StatusChip>;
};

// ── Segmented filter group ───────────────────────────────────────────────────

type FilterGroupProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
};

const FilterGroup = ({ label, value, onChange, options }: FilterGroupProps) => (
  <FilterGroupRoot>
    <FilterLabel>{label}</FilterLabel>
    <Segment>
      {options.map((opt) => (
        <SegmentButton key={opt.value} $active={value === opt.value} onClick={() => onChange(opt.value)} type="button">
          {opt.label}
        </SegmentButton>
      ))}
    </Segment>
  </FilterGroupRoot>
);

// ── Styled ───────────────────────────────────────────────────────────────────

const KpiRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
}));

const FiltersRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 24,
  alignItems: 'flex-end',
}));

const FilterGroupRoot = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}));

const FilterLabel = styled('span')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
}));

const Segment = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  padding: 3,
  gap: 2,
  borderRadius: 10,
  backgroundColor: theme.palette.neutral[15],
}));

const SegmentButton = styled('button', { shouldForwardProp: (p) => p !== '$active' })<{ $active: boolean }>(
  ({ theme, $active }) => ({
    border: 'none',
    cursor: 'pointer',
    padding: '6px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: '"Poppins Medium", sans-serif',
    color: $active ? theme.palette.neutral.white : theme.palette.neutral[70],
    backgroundColor: $active ? theme.palette.blue[60] : 'transparent',
    transition: 'background-color 0.15s ease',
  }),
);

const TamSelect = styled('select')(({ theme }) => ({
  height: 38,
  minWidth: 180,
  padding: '0 12px',
  borderRadius: 10,
  border: `1px solid ${theme.palette.neutral[15]}`,
  backgroundColor: theme.palette.neutral.white,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  color: theme.palette.neutral[70],
  cursor: 'pointer',
}));

const TabsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 4,
  borderBottom: `1px solid ${theme.palette.neutral[15]}`,
}));

const TabButton = styled('button', { shouldForwardProp: (p) => p !== '$active' })<{ $active: boolean }>(
  ({ theme, $active }) => ({
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: '"Poppins Medium", sans-serif',
    color: $active ? theme.palette.blue[60] : theme.palette.neutral[60],
    borderBottom: `2px solid ${$active ? theme.palette.blue[60] : 'transparent'}`,
    marginBottom: -1,
  }),
);

const Banner = styled('div', { shouldForwardProp: (p) => p !== '$tone' })<{ $tone: 'warning' | 'danger' }>(
  ({ theme, $tone }) => ({
    fontSize: 13,
    padding: '10px 14px',
    borderRadius: 8,
    color: $tone === 'danger' ? '#B90A00' : '#C17A00',
    backgroundColor: $tone === 'danger' ? theme.palette.red[20] : '#FDFAF0',
  }),
);

const TwoLine = styled('div', { shouldForwardProp: (p) => p !== '$right' })<{ $right?: boolean }>(({ $right }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  alignItems: $right ? 'flex-end' : 'flex-start',
  '& strong': { fontSize: 12.5, fontWeight: 700, color: '#212121' },
  '& span': {
    fontSize: 11,
    color: '#6B6B7E',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
}));

const ChipStack = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  alignItems: 'flex-start',
}));
